import { useState, useMemo, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { useShallow } from "zustand/shallow";
import * as yup from "yup";
import Icon from "@mdi/react";
import { v4 as uuidv4 } from "uuid";
import { Box } from "@mui/system";
import {
  Avatar,
  Chip,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  mdiAccountGroupOutline,
  mdiAccountMultiplePlusOutline,
  mdiAccountPlusOutline,
  mdiExitToApp,
  mdiLinkVariant,
  mdiTimelineClockOutline,
} from "@mdi/js";

import AppBar from "components/AppBar";
import AsyncAvatar from "components/AsyncAvatar";
import AvatarPicker from "components/AvatarPicker";
import ConfirmDialog from "components/ConfirmDialog";
import ContrastIcon from "components/ContrastIcon";
import ModalInput from "components/ModalInput";
import PageLayout from "components/PageLayout";
import useNotifier from "hooks/useNotifier";
import getNameInitials from "utils/getNameInitials";
import getNounCount from "utils/getNounCount";
import resizeAvatarImage from "utils/resizeAvatarImage";
import { removeArrayItem } from "utils/arrayOfObjectsOperations";
import compareString from "utils/compareString";
import StorageApi, { StorageBuckets } from "api/storage";
import FriendApi from "api/friend";
import GroupApi, { GroupRole } from "api/group";
import { addGroupRole, removeGroupRole, getUserRolesCount, hasGroupRole } from "utils/groupRoles";
import useBoundStore from "store";
import { AugmentedGroup } from "store/appDataSlice";
import VirtualizedList from "components/VirtualizedList";
import UserActionsDialog, { GroupUser, UserActionItem } from "./UserActionsDialog";
import styles from "./groupSettings.module.scss";

type DialogType = "name" | "leave" | "actions";

export interface GroupSettingsProps {
  onAddUsers: () => void;
  onBack?: () => void;
  onInviteLink?: () => void;
  onLeaveGroup?: () => void;
  onViewTimeline: (group: AugmentedGroup) => void;
}

const validationSchema = yup.object().shape({
  inputValue: yup.string().required("Group name is required").min(2, "Group name must be longer than 2 characters"),
});

function GroupSettings(props: GroupSettingsProps) {
  const { onAddUsers, onBack, onInviteLink, onLeaveGroup, onViewTimeline } = props;
  const [groupImage, setGroupImage] = useState<Blob | null>(null);
  const [dialogType, setDialogType] = useState<DialogType | null>(null);
  const [userActions, setUserActions] = useState<UserActionItem[]>([]);
  const [activeUser, setActiveUser] = useState<GroupUser | null>(null);
  const userAuth = useBoundStore((state) => state.user.auth);
  const userProfile = useBoundStore((state) => state.user.profile);
  const { isMobileDevice, smallAvatars } = useBoundStore(
    useShallow((state) => ({
      isMobileDevice: state.miscellaneous.isMobileDevice,
      smallAvatars: state.miscellaneous.smallAvatars,
    }))
  );
  const selected = useBoundStore((state) => state.selected);
  const { groups, friends, friendRequests, groupsUsers, setAppData } = useBoundStore(
    useShallow((state) => ({
      groups: state.appData.groups,
      groupsUsers: state.appData.groupsUsers,
      friends: state.appData.friends,
      friendRequests: state.appData.friendRequests,
      setAppData: state.appData.set,
    }))
  );
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const { showNotification } = useNotifier();
  const friendApi = useMemo(() => new FriendApi(showNotification), [showNotification]);
  const groupApi = useMemo(() => new GroupApi(showNotification), [showNotification]);
  const storageApi = useMemo(() => new StorageApi(showNotification), [showNotification]);
  const group = groups.find((g) => g.id === selected.groupId);

  /**
   * Check if the selected user is already a friend and if not
   * add that action to the actions array
   *
   * @param uid
   * @returns true if user is friend otherwise false
   */
  const isAlreadyFriendOrRequestSent = (uid: number): boolean => {
    const friend = friends.find((f) => f.uid === uid);
    const request = friendRequests.find((r) => r.uid === uid);

    return Boolean(friend || request);
  };

  /**
   * Handle change group name
   *
   * @param newName
   */
  async function handleChangeGroupName(newName: string): Promise<void> {
    if (group) {
      await groupApi.updateGroupName(group.id, newName);
    }
  }

  /**
   * Handle leave group
   */
  async function handleLeaveGroup(): Promise<void> {
    if (group) {
      // If the user is the last member, the group will be deleted server-side.
      // Delete the group avatar from storage beforehand since direct storage.objects
      // deletion is not allowed from SQL triggers.
      if (group.users.length === 1 && group.avatar) {
        await storageApi.deleteFiles(StorageBuckets.GROUPS_AVATARS, [group.avatar, `s-${group.avatar}`]);
      }

      const { error } = await groupApi.leaveGroup(group.id);

      if (error) {
        navigate(-1);
      } else {
        const updatedGroups = removeArrayItem(group.id, groups);
        setAppData({
          groups: updatedGroups,
        });
        selected.set({ groupId: null, eventId: null });

        if (onLeaveGroup) {
          onLeaveGroup();
        }

        if (isMobileDevice) {
          navigate(-3);
        }
      }
    }
  }

  /**
   * Show menu option when clicking on a user
   *
   * @param selectedUser
   */
  function handleSelectUser(selectedUser: GroupUser): void {
    const actions = [];
    if (myUser.admin) {
      // Add action to remove user
      actions.push({
        label: `Remove ${selectedUser.name}`,
        handler: () => handleRemoveUser(selectedUser),
      });

      // Add actions to promote/dissmiss admin
      if (!selectedUser.admin)
        actions.push({
          label: "Make group admin",
          handler: () => handleMakeGroupAdmin(selectedUser),
        });
      else {
        actions.push({
          label: "Dismiss as admin",
          handler: () => handleDismissAsAdmin(selectedUser),
        });
      }
    }

    if (!isAlreadyFriendOrRequestSent(selectedUser.uid)) {
      actions.push({
        label: "Send friend request",
        handler: () => handleSendFriendRequest(selectedUser),
      });
    }

    if (actions.length === 0) {
      return;
    }

    setActiveUser(selectedUser);
    setUserActions(actions);
    handleOpenDialog("actions");
  }

  /**
   * Handle send friend request
   *
   * @param selectedUser
   */
  async function handleSendFriendRequest(selectedUser: GroupUser): Promise<void> {
    await friendApi.sendFriendRequest(userAuth!.id, selectedUser.uid);
  }

  /**
   * Handle add or remove a role from a user
   *
   * @param role
   * @param checked
   */
  async function handleUpdateUserRole(role: GroupRole, checked: boolean): Promise<void> {
    if (!group || !activeUser) return;

    const newRoles = checked
      ? addGroupRole(group.roles, activeUser.uid, role)
      : removeGroupRole(group.roles, activeUser.uid, role);

    const { error } = await groupApi.updateGroupRoles(group.id, newRoles);

    if (!error) {
      const updatedGroups = groups.map((g) => (g.id === group.id ? { ...g, roles: newRoles } : g));
      setAppData({ groups: updatedGroups });
    }
  }

  /**
   * Handle add group admin
   *
   * @param selectedUser
   */
  async function handleMakeGroupAdmin(selectedUser: GroupUser): Promise<void> {
    if (group) {
      const newAdmins = [...group.admins, selectedUser.uid];

      await groupApi.addOrRemoveGroupAdmins(group.id, newAdmins);
    }
  }

  /**
   * Handle remove group admin
   *
   * @param selectedUser
   */
  async function handleDismissAsAdmin(selectedUser: GroupUser): Promise<void> {
    if (group) {
      const newAdmins = group.admins.filter((admin) => admin !== selectedUser.uid);

      await groupApi.addOrRemoveGroupAdmins(group.id, newAdmins);
    }
  }

  /**
   * Handle remove user from group
   *
   * @param selectedUser
   */
  async function handleRemoveUser(selectedUser: GroupUser): Promise<void> {
    if (group) {
      const newUsers = group.users.filter((user) => user !== selectedUser.uid);
      const newAdmins = group.admins.filter((user) => user !== selectedUser.uid);

      await groupApi.addOrRemoveGroupUsers(group.id, newUsers, newAdmins);
    }
  }

  /**
   * Save group image to storage bucket
   *
   * @param image
   */
  async function handleSaveGroupAvatar(image: Blob): Promise<void> {
    setGroupImage(null);
    const { bigAvatarImg, smallAvatarImg } = await resizeAvatarImage(image);
    const imageName = `${uuidv4()}.jpg`;

    // Save both big and small group image to storage
    const { error: bigAvatarError } = await storageApi.saveImage(
      bigAvatarImg,
      imageName,
      StorageBuckets.GROUPS_AVATARS
    );
    const { error: smallAvatarError } = await storageApi.saveImage(
      smallAvatarImg,
      `s-${imageName}`,
      StorageBuckets.GROUPS_AVATARS
    );

    if (!bigAvatarError && !smallAvatarError) {
      let deleteError;
      // Remove old group images (both big and small one)
      if (group?.avatar) {
        ({ error: deleteError } = await storageApi.deleteFiles(StorageBuckets.GROUPS_AVATARS, [
          group.avatar,
          `s-${group.avatar}`,
        ]));
      }

      // Update group image with the new one
      if (!deleteError) {
        await groupApi.updateGroupAvatar(group!.id, imageName);

        setGroupImage(image);
      }
    }
  }

  /**
   * Handle delete group image from storage
   */
  async function handleDeleteGroupImage(): Promise<void> {
    if (group?.avatar) {
      const { error } = await storageApi.deleteFiles(StorageBuckets.GROUPS_AVATARS, [
        group.avatar,
        `s-${group.avatar}`,
      ]);

      if (!error) {
        await groupApi.updateGroupAvatar(group!.id, "");
        setGroupImage(null);
      }
    }
  }

  /**
   * Handle open dialog
   */
  function handleOpenDialog(type: DialogType) {
    setDialogType(type);
    navigate(location.pathname);
  }

  /**
   * Handle close dialog
   */
  function handleCloseDialog() {
    setDialogType(null);
    navigate(-1);
  }

  /**
   * Fetch group avatar
   */
  const fetchGroupAvatar = useCallback(async () => {
    if (!groupImage && group?.avatar) {
      const { file } = await storageApi.getFile(group?.avatar, StorageBuckets.GROUPS_AVATARS);

      setGroupImage(file);
    }
  }, [group, groupImage, storageApi]);

  /**
   * Set group avatar on component mount
   */
  useEffect(() => {
    fetchGroupAvatar();
  }, [fetchGroupAvatar]);

  /**
   * On pressing back button close opened dialogs
   */
  useEffect(() => {
    if (navigationType === "POP") {
      setDialogType(null);
    }
  }, [navigationType]);

  /** If group is not defined render null */
  if (!group) {
    return null;
  }

  const groupUsers = groupsUsers
    .filter((user) => group.users.includes(user.uid))
    .sort((a, b) => compareString(a.name ?? "", b.name ?? ""));

  const mappedGroupUsers: GroupUser[] = groupUsers.map((user) => ({
    ...user,
    admin: group?.admins.includes(user.uid),
  }));

  const myUser = mappedGroupUsers.find((user) => user.uid === userAuth?.id)!;

  /** If my user is not defined render null */
  if (!myUser) {
    return null;
  }

  /**
   * Generate items which will be used to display de virtualized list
   *
   * @returns
   */
  function generateVirtualizedList(): JSX.Element[] {
    const vrList: JSX.Element[] = [];
    if (group) {
      // Add avatar or avatar picker if user is admin
      vrList.push(
        <Box m={2} display='flex' justifyContent='center'>
          {myUser.admin ? (
            <AvatarPicker
              id='group-settings-avatar'
              img={groupImage ? URL.createObjectURL(groupImage) : undefined}
              placeholder={getNameInitials(group.name)}
              onSelectImage={handleSaveGroupAvatar}
              onDeleteImage={handleDeleteGroupImage}
            />
          ) : (
            <div className={styles.avatarContainer}>
              <AsyncAvatar
                avatarFile={group.avatar ? smallAvatars[group.avatar] : undefined}
                avatarName={group.avatar}
                bucket={StorageBuckets.GROUPS_AVATARS}
                className={styles.avatar}
                loadOriginal
                placeholder={getNameInitials(group.name)}
              />
            </div>
          )}
        </Box>
      );

      // Add group group list item button
      if (myUser.admin) {
        vrList.push(
          <ListItemButton dense onClick={() => handleOpenDialog("name")}>
            <ListItemIcon>
              <Icon path={mdiAccountGroupOutline} size={1} />
            </ListItemIcon>
            <ListItemText primary='Name' secondary={group.name} />
          </ListItemButton>
        );
      }

      // Add group timeline list item button
      vrList.push(
        <ListItemButton dense onClick={() => onViewTimeline(group)}>
          <ListItemIcon>
            <Icon path={mdiTimelineClockOutline} title='Timeline clock' size={1} />
          </ListItemIcon>
          <ListItemText
            primary='Timeline'
            secondary="Explore the group's event history for a brief overview of past happenings"
          />
        </ListItemButton>
      );

      // Add leave group list item button
      vrList.push(
        <ListItemButton dense onClick={() => handleOpenDialog("leave")}>
          <ListItemIcon>
            <Icon path={mdiExitToApp} title='Leave' size={1} />
          </ListItemIcon>
          <ListItemText primary='Leave group' secondary='Rejoin with admin approval or through an invite link' />
        </ListItemButton>
      );

      // Add invite via link list item button
      if (myUser.admin || hasGroupRole(group, myUser.uid, "manage-invite-link")) {
        vrList.push(
          <ListItemButton dense onClick={onInviteLink}>
            <ListItemIcon>
              <Icon path={mdiLinkVariant} title='Link' size={1} />
            </ListItemIcon>
            <ListItemText
              primary='Invite via link'
              secondary='Anyone with the link can join the group without admin approval'
            />
          </ListItemButton>
        );
      }

      if (myUser.admin) {
        // Add new participants list item button
        vrList.push(
          <ListItemButton onClick={onAddUsers}>
            <ListItemAvatar>
              <Avatar className={styles.addAvatar}>
                <ContrastIcon path={mdiAccountPlusOutline} title='Add' size={1} />
              </Avatar>
            </ListItemAvatar>
            <ListItemText primary='Add participants' secondary={getNounCount("participant", group.users.length)} />
          </ListItemButton>
        );
      } else {
        // Render number of participants as text
        vrList.push(
          <Box px={2} mt={2} mb={1}>
            <Typography variant='body2' color='text.secondary'>
              {getNounCount("participant", group.users.length)}
            </Typography>
          </Box>
        );
      }

      // Render own user on first position in list
      vrList.push(
        <ListItem>
          <ListItemAvatar>
            <AsyncAvatar
              avatarFile={userProfile?.avatar ? smallAvatars[userProfile.avatar] : undefined}
              avatarName={userProfile?.avatar}
              placeholder={getNameInitials(myUser.name)}
              bucket={StorageBuckets.PROFILES_AVATARS}
            />
          </ListItemAvatar>
          <ListItemText className={styles.userName} primary='You' secondary={myUser.tag} />
          <ListItemSecondaryAction>
            <Box display='flex' alignItems='center' gap={2}>
              {myUser.admin ? (
                <Chip variant='outlined' size='small' color='primary' label='Admin' />
              ) : (
                (() => {
                  const rolesCount = getUserRolesCount(group?.roles, myUser.uid);
                  return rolesCount > 0 ? (
                    <Chip
                      variant='outlined'
                      size='small'
                      color='primary'
                      label={`${rolesCount} role${rolesCount > 1 ? "s" : ""}`}
                    />
                  ) : null;
                })()
              )}
            </Box>
          </ListItemSecondaryAction>
        </ListItem>
      );

      // Render group users
      mappedGroupUsers.forEach((user) => {
        const isFriend = isAlreadyFriendOrRequestSent(user.uid);
        const content = (
          <>
            <ListItemAvatar>
              <AsyncAvatar
                avatarFile={user.avatar ? smallAvatars[user.avatar] : undefined}
                avatarName={user.avatar}
                placeholder={getNameInitials(user.name)}
                bucket={StorageBuckets.PROFILES_AVATARS}
              />
            </ListItemAvatar>
            <ListItemText className={styles.userName} primary={user.name} secondary={user.tag} />

            <ListItemSecondaryAction>
              <Box display='flex' alignItems='center' gap={2}>
                {!isFriend ? (
                  <Icon
                    className={styles.accountMultipleOutlineIcon}
                    path={mdiAccountMultiplePlusOutline}
                    size={0.8}
                    title='Send friend request'
                  />
                ) : null}
                {user.admin ? (
                  <Chip variant='outlined' size='small' color='primary' label='Admin' />
                ) : (
                  (() => {
                    const rolesCount = getUserRolesCount(group?.roles, user.uid);
                    return rolesCount > 0 ? (
                      <Chip
                        variant='outlined'
                        size='small'
                        color='primary'
                        label={`${rolesCount} role${rolesCount > 1 ? "s" : ""}`}
                      />
                    ) : null;
                  })()
                )}
              </Box>
            </ListItemSecondaryAction>
          </>
        );

        if (user.uid !== myUser.uid) {
          vrList.push(
            <ListItem disablePadding={myUser.admin || !isFriend} key={user.uid}>
              {myUser.admin || !isFriend ? (
                <ListItemButton onClick={() => handleSelectUser(user)}>{content}</ListItemButton>
              ) : (
                content
              )}
            </ListItem>
          );
        }
      });
    }

    return vrList;
  }

  return (
    <PageLayout appBar={<AppBar title='Group settings' onBack={onBack} />} paddingBottom={false}>
      <VirtualizedList estimateSize={72} rows={generateVirtualizedList()} style={{ listStyle: "none" }} />
      <ModalInput
        open={dialogType === "name"}
        onCancel={handleCloseDialog}
        modal={{ title: "Edit group name" }}
        input={{
          initialValue: group.name,
          label: "Group name",
          icon: mdiAccountGroupOutline,
          validationSchema,
        }}
        onSubmit={handleChangeGroupName}
      />

      <ConfirmDialog
        open={dialogType === "leave"}
        onClose={handleCloseDialog}
        title='Leave group'
        body={<Typography>Are you sure do you want to leave {group.name} group?`</Typography>}
        closeLabel='Cancel'
        confirmLabel='Leave'
        onConfirm={handleLeaveGroup}
      />
      <UserActionsDialog
        actions={userActions}
        onClose={handleCloseDialog}
        onRoleChange={handleUpdateUserRole}
        open={dialogType === "actions"}
        roles={group.roles}
        showManageRoles={!!myUser.admin && !activeUser?.admin}
        user={activeUser}
      />
    </PageLayout>
  );
}

export default GroupSettings;
